import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useNotifications } from '../NotificationContext';
import EventCardBack from '../EventCardBack';
import { goToSignup } from '../../utils/guestSearch';

const BellIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

/**
 * Top-right Remind Me control for sports nextEvent and artist concerts.
 */
export default function HubRemindButton({ epgEvent, onOpenChange }) {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const {
    isEventSubscribed,
    getEventSubscription,
    subscribeToEvent,
    unsubscribe,
  } = useNotifications();

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [showLoginHint, setShowLoginHint] = useState(false);

  if (!epgEvent) return null;

  const eventId = epgEvent.dbId
    || (epgEvent.platform === 'EPG' ? epgEvent.id : null)
    || null;
  const canRemind = Boolean(eventId)
    || Boolean(epgEvent.start || epgEvent.startsAt);
  if (!canRemind) return null;

  const start = epgEvent.start || epgEvent.startsAt
    ? new Date(epgEvent.start || epgEvent.startsAt)
    : null;
  const end = epgEvent.end ? new Date(epgEvent.end) : null;
  const now = new Date();
  const isLive = Boolean(start && end && start <= now && end >= now);
  if (isLive) return null;

  const isSubscribed = eventId ? isEventSubscribed(eventId) : false;
  const subscriptionData = eventId ? getEventSubscription(eventId) : null;

  const setOpen = (open) => {
    setIsPanelOpen(open);
    if (typeof onOpenChange === 'function') onOpenChange(open);
  };

  const handleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) {
      setShowLoginHint(true);
      window.setTimeout(() => setShowLoginHint(false), 3000);
      return;
    }
    setOpen(true);
  };

  const handleSubscribe = async (channels, isMainEvent) => {
    if (!eventId) return;
    if (isSubscribed && subscriptionData) {
      await unsubscribe(subscriptionData.id);
    }
    if (channels.length === 0) return;
    await subscribeToEvent(eventId, channels, isMainEvent);
  };

  return (
    <div className="entity-hub-remind">
      <button
        type="button"
        className={`entity-hub-remind-btn${isSubscribed ? ' is-active' : ''}`}
        onClick={handleOpen}
        aria-label={isSubscribed ? 'Reminder enabled' : 'Remind me'}
        title={isSubscribed ? 'Reminder enabled' : 'Remind me'}
      >
        <BellIcon />
      </button>

      {showLoginHint ? (
        <div className="entity-hub-remind-hint" role="status">
          <span>Log in to get notified about this event</span>
          <button
            type="button"
            className="entity-hub-remind-hint-cta"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              goToSignup(navigate);
            }}
          >
            Sign up
          </button>
        </div>
      ) : null}

      {isPanelOpen ? (
        <div
          className="entity-hub-remind-panel"
          role="dialog"
          aria-label="Notification preferences"
          onClick={(e) => e.stopPropagation()}
        >
          <EventCardBack
            event={epgEvent}
            subscriptionData={subscriptionData}
            allowPush={Boolean(eventId)}
            onSubscribe={handleSubscribe}
            onCancel={() => setOpen(false)}
          />
        </div>
      ) : null}
    </div>
  );
}
